// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 09-data-brokering.md
// @greenlight-spec: decisions/0027-integration-grant-approval-policy-placement.md
// @greenlight-spec: decisions/0039-injected-grant-is-disclosure.md
// @greenlight-spec: decisions/0029-principal-scoped-credential-grants.md
import * as React from 'react';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { CredentialRequiredGroup, IntegrationListItem } from '@greenlight/shared';

import {
  CredentialSecretInputs,
  EMPTY_SECRET_DRAFT,
  clientIdLabelFor,
  clientIdPlaceholderFor,
  composeSecretDraft,
  isSecretDraftEmpty,
  secretDraftError,
  secretNounFor,
  secretShapeFor,
  type SecretDraft,
} from './CredentialSecretInputs';
import {
  RequiredGroupsPicker,
  sameRequiredGroups,
  toRequiredGroupRefs,
} from './RequiredGroupsPicker';

/** The upstream client id a user passthrough credential's stored auth_config names. */
function storedClientId(authConfig: Record<string, unknown> | null | undefined): string {
  const clientId = authConfig?.['client_id'];
  return typeof clientId === 'string' ? clientId : '';
}

/**
 * The role a user passthrough credential's stored auth_config pins (Snowflake).
 * It is fixed at creation, since each user consented to it when they connected,
 * so the dialog shows it read-only and an edit carries it through unchanged.
 */
function storedRole(authConfig: Record<string, unknown> | null | undefined): string | undefined {
  const role = authConfig?.['role'];
  return typeof role === 'string' && role !== '' ? role : undefined;
}

export interface EditCredentialDialogProps {
  integration: IntegrationListItem | null;
  credentialSlug: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a credential's secret is replaced, so a test result kept for the old one is dropped. */
  onSecretReplaced?: (integrationId: string, slug: string) => void;
}

export function EditCredentialDialog({
  integration,
  credentialSlug,
  open,
  onOpenChange,
  onSecretReplaced,
}: EditCredentialDialogProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const updateCredential = api.integrations.updateCredential.useMutation();
  const replaceSecret = api.integrations.replaceSecret.useMutation();
  const setCredentialPolicy = api.integrations.setCredentialPolicy.useMutation();
  const setRequiredGroups = api.integrations.setCredentialRequiredGroups.useMutation();
  const [requiredGroups, setRequiredGroupsDraft] = React.useState<CredentialRequiredGroup[]>([]);
  const [scope, setScope] = React.useState('');
  const [upstreamClientId, setUpstreamClientId] = React.useState('');
  const [secretDraft, setSecretDraft] = React.useState<SecretDraft>(EMPTY_SECRET_DRAFT);
  const [approvalAuto, setApprovalAuto] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const credential = React.useMemo(
    () => (integration?.credentials ?? []).find((item) => item.slug === credentialSlug) ?? null,
    [integration, credentialSlug],
  );

  React.useEffect(() => {
    if (open) {
      setScope(credential?.scope ?? '');
      setUpstreamClientId(storedClientId(credential?.auth_config));
      setSecretDraft(EMPTY_SECRET_DRAFT);
      setApprovalAuto(credential?.approval_mode === 'auto');
      setRequiredGroupsDraft(credential?.required_groups ?? []);
      setError(null);
    }
  }, [open, credential]);

  const isInjected = integration?.delivery_mode === 'injected';
  const isPassthrough = integration?.auth_mode === 'user_delegated';
  const secretShape = secretShapeFor(
    integration?.auth_mode,
    integration?.token_endpoint_auth_method,
  );
  const clientIdLabel = clientIdLabelFor(
    integration?.catalog_key,
    integration?.auth_mode,
    integration?.auth_config?.['mechanism'],
  );
  const clientIdPlaceholder = clientIdPlaceholderFor(
    integration?.catalog_key,
    integration?.auth_mode,
    integration?.auth_config?.['mechanism'],
  );
  const secretNoun = isPassthrough
    ? 'client secret'
    : secretNounFor(secretShape, integration?.auth_mode ?? '');
  const grantsQuery = api.permissions.queue.useQuery(
    ['credential-grants', integration?.id ?? '', credentialSlug ?? ''],
    { query: { integrationId: integration?.id ?? '', status: 'granted', limit: 100 } },
    {
      queryKey: ['credential-grants', integration?.id ?? '', credentialSlug ?? ''],
      enabled: open && isInjected && !isSecretDraftEmpty(secretDraft) && integration != null,
    },
  );
  const grantsData = grantsQuery.data;
  const grantsPage = grantsData?.status === 200 ? grantsData.body : null;
  const grantsFailed = !grantsQuery.isLoading && grantsPage == null;
  const affectedAppNames = grantsPage
    ? Array.from(
        new Set(
          grantsPage.items
            .filter((grant) => grant.credential_slug === credentialSlug)
            .map((grant) => grant.app_name ?? grant.requested_by),
        ),
      ).sort((a, b) => a.localeCompare(b))
    : [];
  const affectedAppsTruncated = grantsPage != null && grantsPage.next_cursor != null;

  const secretBacked = credential != null && credential.vault_key !== null;
  const trimmedScope = scope.trim();
  const trimmedClientId = upstreamClientId.trim();
  const pinnedRole = storedRole(credential?.auth_config);
  const clientIdChanged =
    isPassthrough &&
    credential != null &&
    trimmedClientId !== storedClientId(credential.auth_config);
  const scopeChanged =
    credential != null && (trimmedScope !== credential.scope.trim() || clientIdChanged);
  const secretChanged = secretBacked && !isSecretDraftEmpty(secretDraft);
  const approvalChanged =
    credential != null && approvalAuto !== (credential.approval_mode === 'auto');
  const requiredGroupsChanged =
    credential != null && !sameRequiredGroups(requiredGroups, credential.required_groups);
  const changed = scopeChanged || secretChanged || approvalChanged || requiredGroupsChanged;
  const pending =
    updateCredential.isPending ||
    replaceSecret.isPending ||
    setCredentialPolicy.isPending ||
    setRequiredGroups.isPending;

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!integration || !credentialSlug || !credential) return;
    if (!trimmedScope) {
      setError('Scope is required.');
      return;
    }
    if (isPassthrough && !trimmedClientId) {
      setError('The upstream client (application) ID is required.');
      return;
    }
    if (secretChanged) {
      const draftError = secretDraftError(secretDraft, {
        shape: secretShape,
        clientIdLabel,
        secretNoun,
        required: false,
      });
      if (draftError) {
        setError(draftError);
        return;
      }
    }
    // Sequential, not Promise.all: each write is independent, so a failure
    // must report exactly what already saved — a parallel batch can land the
    // vault rotation and then claim nothing was saved. Fields whose write
    // succeeded reset their dirty state, so a retry re-sends only the failure.
    const steps: Array<{
      label: string;
      changed: boolean;
      run: () => Promise<{ status: number }>;
      settle: () => void;
    }> = [
      {
        label: isPassthrough ? 'scope and upstream client' : 'scope',
        changed: scopeChanged,
        run: () =>
          updateCredential.mutateAsync({
            params: { id: integration.id, slug: credentialSlug },
            body: {
              scope: trimmedScope,
              ...(isPassthrough
                ? {
                    authConfig: {
                      client_id: trimmedClientId,
                      ...(pinnedRole !== undefined ? { role: pinnedRole } : {}),
                    },
                  }
                : {}),
            },
          }),
        settle: () => undefined,
      },
      {
        label: 'secret',
        changed: secretChanged,
        run: () =>
          replaceSecret.mutateAsync({
            params: { id: integration.id, slug: credentialSlug },
            body: { secret: composeSecretDraft(secretDraft, secretShape) },
          }),
        settle: () => {
          setSecretDraft(EMPTY_SECRET_DRAFT);
          onSecretReplaced?.(integration.id, credentialSlug);
        },
      },
      {
        label: 'approval policy',
        changed: approvalChanged,
        run: () =>
          setCredentialPolicy.mutateAsync({
            params: { id: integration.id, slug: credentialSlug },
            body: { approvalMode: approvalAuto ? 'auto' : 'manual' },
          }),
        settle: () => undefined,
      },
      {
        label: 'required groups',
        changed: requiredGroupsChanged,
        run: () =>
          setRequiredGroups.mutateAsync({
            params: { id: integration.id, slug: credentialSlug },
            body: { groups: toRequiredGroupRefs(requiredGroups) },
          }),
        settle: () => undefined,
      },
    ];
    const saved: string[] = [];
    for (const step of steps.filter((candidate) => candidate.changed)) {
      let ok = false;
      try {
        const res = await step.run();
        ok = res.status < 300;
      } catch {
        ok = false;
      }
      if (!ok) {
        await queryClient.invalidateQueries({ queryKey: ['integrations'] });
        setError(
          saved.length > 0
            ? `The ${step.label} update failed. Already saved: ${saved.join(', ')} — retrying re-sends only what failed.`
            : `The ${step.label} update failed. Nothing was saved. Please try again.`,
        );
        return;
      }
      step.settle();
      saved.push(step.label);
    }
    toast.success(`Credential "${credentialSlug}" updated.`);
    await queryClient.invalidateQueries({ queryKey: ['integrations'] });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit credential</DialogTitle>
          <DialogDescription>
            {integration
              ? `Update this credential on ${integration.name}. Leave the secret blank to keep its current value.`
              : ''}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="edit-cred-slug">Slug</Label>
            <Input
              id="edit-cred-slug"
              value={credentialSlug ?? ''}
              disabled
              className="font-mono"
            />
            <p className="text-[11.5px] leading-snug text-muted-foreground">
              The slug is fixed because apps bind grants to it.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-cred-scope">Scope</Label>
            <Input
              id="edit-cred-scope"
              value={scope}
              onChange={(event) => setScope(event.target.value)}
              placeholder="What this key grants (free text)"
            />
          </div>
          {isPassthrough && (
            <div className="space-y-1.5">
              <Label htmlFor="edit-cred-upstream-client-id">{clientIdLabel}</Label>
              <Input
                id="edit-cred-upstream-client-id"
                value={upstreamClientId}
                onChange={(event) => setUpstreamClientId(event.target.value)}
                placeholder={
                  clientIdPlaceholder ?? 'Application (client) ID of the upstream registration'
                }
                className="font-mono"
                autoComplete="off"
              />
              <p className="text-[11.5px] leading-snug text-muted-foreground">
                The upstream registration this credential presents, as each signed-in user.
              </p>
            </div>
          )}
          {isPassthrough && pinnedRole !== undefined && (
            <div className="space-y-1">
              <p className="text-[12px] font-medium text-foreground">Role</p>
              <MonoValue>{pinnedRole}</MonoValue>
              <p className="text-[11.5px] leading-snug text-muted-foreground">
                Fixed once saved: each person consented to this role when they connected. Add
                another credential for another role.
              </p>
            </div>
          )}
          {secretBacked ? (
            <CredentialSecretInputs
              idPrefix="edit-cred"
              shape={secretShape}
              clientIdLabel={clientIdLabel}
              clientIdPlaceholder={clientIdPlaceholder}
              secretNoun={secretNoun}
              draft={secretDraft}
              onChange={setSecretDraft}
              replacement
            />
          ) : (
            <p className="rounded-gl border border-dashed border-border px-3 py-2 text-[11.5px] leading-snug text-muted-foreground">
              No secret is stored for this credential; Greenlight uses its platform identity.
            </p>
          )}

          {secretChanged &&
            integration &&
            (isInjected ? (
              <div
                className={cn(
                  'rounded-gl border border-gl-amber/30 bg-gl-amber/10 px-3 py-2.5',
                  'text-[12.5px] leading-relaxed text-gl-amber-text',
                )}
                role="status"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <div className="space-y-1">
                    <p className="font-medium">
                      Running apps keep the old value until their next deploy.
                    </p>
                    {grantsQuery.isLoading ? (
                      <p>Checking which apps use this credential…</p>
                    ) : grantsFailed ? (
                      <p>Couldn’t load affected apps. Any granted app may keep the old value.</p>
                    ) : affectedAppNames.length > 0 ? (
                      <p>
                        Affected {affectedAppNames.length === 1 ? 'app' : 'apps'}:{' '}
                        <span className="font-medium">{affectedAppNames.join(', ')}</span>
                        {affectedAppsTruncated ? ' and others' : ''}.
                      </p>
                    ) : (
                      <p>No apps currently hold a grant on this credential.</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-[12.5px] leading-relaxed text-muted-foreground">
                Proxied delivery: the new secret takes effect on the next call through the proxy.
              </p>
            ))}

          <div className="space-y-1.5 rounded-gl border border-border bg-muted/30 p-3">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="edit-cred-approval" className="cursor-pointer">
                Auto-approve grants
              </Label>
              <Switch
                id="edit-cred-approval"
                checked={approvalAuto}
                onCheckedChange={setApprovalAuto}
                aria-label="Auto-approve grants for this credential"
              />
            </div>
            <p className="text-[11.5px] leading-snug text-muted-foreground">
              On, grants auto-approve at merge. Off, grants land in IT’s review queue.
            </p>
            <div className="border-t border-border pt-3">
              <RequiredGroupsPicker
                idPrefix="edit-cred"
                value={requiredGroups}
                onChange={setRequiredGroupsDraft}
                enabled={open}
              />
            </div>
          </div>
          {error && <p className="text-[11.5px] text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending || !changed}>
              {pending ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
